import { Button, NativeSelect, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { MessageQueryParams, MessageResponse } from '@/api';
import { VerdictTag } from '@/components/Inbox/VerdictTag';
import { openConfirmDelete } from '@/components/Ui/ConfirmDelete';
import { DataTable } from '@/components/Ui/DataTable';
import { Section } from '@/components/Ui/Section';
import { Tag } from '@/components/Ui/Tag';
import { useUndoToast } from '@/components/Ui/Toast';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { isSpamFor, useVerdictPreference, type Verdict } from '@/lib/message-prefs';
import { downloadText } from '@/lib/messages';
import { formatNumber } from '@/lib/money';
import { absoluteTime, relativeTime } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import classes from './FormMessages.module.css';
import { firstLineOf, senderOf } from './message-text';

const PAGE = 25;

export interface FormMessagesListProps {
  formId: string;
  formTitle: string;
  encrypted: boolean;
  retentionDays?: number | null;
  filterSpam?: boolean;
}

export function FormMessagesList({
  formId,
  formTitle,
  encrypted,
  retentionDays,
  filterSpam,
}: FormMessagesListProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const notify = useUndoToast();

  const [q, setQ] = useState('');
  const [debouncedQ] = useDebouncedValue(q.trim(), 300);
  const [verdict, setVerdict] = useVerdictPreference();
  const [rows, setRows] = useState<MessageResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const generation = useRef(0);

  const query = (after?: MessageResponse): MessageQueryParams => ({
    form_id: formId,
    limit: PAGE,
    q: debouncedQ || undefined,
    is_spam: isSpamFor(verdict),
    ...(after ? { before: after.created_at, before_id: after.id } : {}),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: query() reads exactly these
  useEffect(() => {
    let live = true;
    generation.current += 1;
    setLoading(true);
    setLoadingMore(false);
    setSelected(new Set());
    useRustyState
      .getState()
      .api.listMessages(query())
      .then((res) => {
        if (!live) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch(() => {
        if (!live) return;
        setRows([]);
        setTotal(0);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [formId, debouncedQ, verdict]);

  const loadMore = async () => {
    const started = generation.current;
    setLoadingMore(true);
    try {
      const res = await useRustyState.getState().api.listMessages(query(rows[rows.length - 1]));
      if (started !== generation.current) return;
      setRows((current) => {
        const seen = new Set(current.map((r) => r.id));
        return [...current, ...res.data.filter((r) => !seen.has(r.id))];
      });
      setTotal(res.total);
    } catch {
      // The API layer has shown the error.
    } finally {
      if (started === generation.current) setLoadingMore(false);
    }
  };

  const refreshCounts = () => {
    const state = useRustyState.getState();
    void state.refreshUnreadCount();
    void state.refreshFormsSummary();
  };

  const ids = [...selected];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = !allSelected && rows.some((r) => selected.has(r.id));

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectedRows = rows.filter((r) => selected.has(r.id));

  const markSpam = async (isSpam: boolean) => {
    const changing = selectedRows.filter((r) => r.is_spam !== isSpam).map((r) => r.id);
    if (changing.length === 0) return;
    setBusy(true);
    try {
      const res = await useRustyState
        .getState()
        .api.bulkUpdateMessages({ message_ids: changing, is_spam: isSpam });
      const failed = new Set(res.failed);
      const changed = new Set(changing.filter((id) => !failed.has(id)));
      setRows(
        rows.map((r) => (changed.has(r.id) ? { ...r, is_spam: isSpam, user_marked_spam: true } : r))
      );
      setSelected(failed);
      const parts = [
        isSpam
          ? t('formMessages.markedSpam', { count: res.succeeded })
          : t('formMessages.markedNotSpam', {
              count: res.succeeded,
              notified: res.notified_recipients,
            }),
      ];
      if (res.integrations_resent > 0)
        parts.push(t('inbox.bulkIntegrationsResent', { count: res.integrations_resent }));
      if (res.auto_replies_sent > 0)
        parts.push(t('inbox.bulkAutoRepliesSent', { count: res.auto_replies_sent }));
      if (failed.size > 0) parts.push(t('formMessages.failedSome', { count: failed.size }));
      notify(parts.join(' '));
      refreshCounts();
    } catch {
      // The API layer has shown the error.
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = () =>
    openConfirmDelete({
      label: t('formMessages.deleteTitle', { count: ids.length }),
      description: t('formMessages.deleteDescription'),
      confirmLabel: t('formMessages.deleteConfirm', { count: ids.length }),
      onConfirm: async () => {
        const res = await useRustyState.getState().api.bulkDeleteMessages(ids);
        const failed = new Set(res.failed);
        setRows((current) => current.filter((r) => !selected.has(r.id) || failed.has(r.id)));
        setTotal((n) => Math.max(0, n - res.succeeded));
        setSelected(failed);
        notify(t('formMessages.deleted', { count: res.succeeded }));
        refreshCounts();
      },
    });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = await useRustyState.getState().api.downloadMessagesCsv({
        form_id: formId,
        is_spam: isSpamFor(verdict),
        q: debouncedQ || undefined,
      });
      const slug = formTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      downloadText(blob, `${slug || 'form'}-messages.csv`);
    } catch {
      notify(t('formMessages.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const messagePath = (row: MessageResponse) =>
    `/account/messages/${row.id}?form=${encodeURIComponent(formId)}`;
  const open = (row: MessageResponse) => navigate(createLanguageURL(messagePath(row)));
  const isEncrypted = (row: MessageResponse) => encrypted || (!row.data && !!row.data_encrypted);

  return (
    <Section
      title={t('formMessages.listTitle')}
      actions={
        <div className={classes.toolbar}>
          <TextInput
            type="search"
            aria-label={t('formMessages.searchLabel')}
            placeholder={t('formMessages.search')}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            w={260}
          />
          <NativeSelect
            aria-label={t('formMessages.verdictLabel')}
            value={verdict}
            onChange={(e) => setVerdict(e.currentTarget.value as Verdict)}
            data={[
              { value: '', label: t('formMessages.verdictAll') },
              { value: 'not_spam', label: t('formMessages.verdictHam') },
              { value: 'spam', label: t('formMessages.verdictSpam') },
            ]}
          />
          <Button variant="default" onClick={() => void exportCsv()} loading={exporting}>
            {t('formMessages.exportCsv')}
          </Button>
        </div>
      }
    >
      {selected.size > 0 && (
        <div
          className={classes.bulk}
          data-bulk-bar
          role="toolbar"
          aria-label={t('formMessages.selected', { count: selected.size })}
        >
          <span className={classes.bulkCount}>
            {t('formMessages.selected', { count: selected.size })}
          </span>
          {selectedRows.some((r) => !r.is_spam) && (
            <Button size="xs" variant="default" onClick={() => void markSpam(true)} disabled={busy}>
              {t('formMessages.markSpam')}
            </Button>
          )}
          {selectedRows.some((r) => r.is_spam) && (
            <Button
              size="xs"
              variant="default"
              onClick={() => void markSpam(false)}
              disabled={busy}
            >
              {t('formMessages.markNotSpam')}
            </Button>
          )}
          <Button size="xs" variant="danger" onClick={deleteSelected} disabled={busy}>
            {t('formMessages.deleteSelected')}
          </Button>
          <Button size="xs" variant="subtle" onClick={() => setSelected(new Set())}>
            {t('formMessages.clearSelection')}
          </Button>
        </div>
      )}
      <DataTable
        caption={t('formMessages.listTitle')}
        rows={loading ? [] : rows}
        rowKey={(r) => r.id}
        onRowClick={open}
        empty={
          loading
            ? t('ui.loading')
            : debouncedQ || verdict !== ''
              ? t('formMessages.emptySearch')
              : t('formMessages.empty')
        }
        columns={[
          {
            key: 'select',
            header: (
              <input
                type="checkbox"
                className={classes.check}
                aria-label={t('formMessages.selectAll')}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={() =>
                  setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))
                }
              />
            ),
            render: (row) => {
              const from = isEncrypted(row)
                ? t('formMessages.encryptedFrom')
                : (senderOf(row) ?? t('formMessages.unknownSender'));
              return (
                <input
                  type="checkbox"
                  className={classes.check}
                  aria-label={t('formMessages.selectRow', { from })}
                  checked={selected.has(row.id)}
                  onChange={() => toggle(row.id)}
                />
              );
            },
          },
          {
            key: 'from',
            header: t('formMessages.colFrom'),
            render: (row) => (
              <UiLink to={messagePath(row)}>
                <span
                  className={row.is_read === false && !row.is_spam ? classes.unread : undefined}
                >
                  {isEncrypted(row)
                    ? t('formMessages.encryptedFrom')
                    : (senderOf(row) ?? t('formMessages.unknownSender'))}
                </span>
              </UiLink>
            ),
          },
          {
            key: 'line',
            header: t('formMessages.colFirstLine'),
            render: (row) =>
              isEncrypted(row) ? (
                <Tag variant="enc">{t('formMessages.encryptedLine')}</Tag>
              ) : (
                <span className={classes.firstLine}>{firstLineOf(row)}</span>
              ),
          },
          {
            key: 'verdict',
            header: t('formMessages.colVerdict'),
            render: (row) => <VerdictTag message={row} filterOn={filterSpam} />,
          },
          {
            key: 'received',
            header: t('formMessages.colReceived'),
            render: (row) => (
              <span className={classes.nowrap} title={absoluteTime(row.created_at, i18n.language)}>
                {relativeTime(row.created_at, i18n.language)}
              </span>
            ),
          },
        ]}
      />
      {!loading && rows.length > 0 && (
        <div className={classes.footer}>
          <span className={classes.rh}>
            {retentionDays
              ? t('formMessages.shownKept', {
                  shown: formatNumber(rows.length, i18n.language),
                  total: formatNumber(total, i18n.language),
                  days: retentionDays,
                })
              : t('formMessages.shown', {
                  shown: formatNumber(rows.length, i18n.language),
                  total: formatNumber(total, i18n.language),
                })}
          </span>
          {rows.length < total && (
            <Button
              size="xs"
              variant="default"
              onClick={() => void loadMore()}
              loading={loadingMore}
            >
              {t('formMessages.loadMore')}
            </Button>
          )}
        </div>
      )}
    </Section>
  );
}
